import React, { useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../utils/translations';
import Container from '../common/Container';
import { FaGithub, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaFilter } from 'react-icons/fa';
import useProjectFilter from '../../hooks/useProjectFilter';
import useDescriptionToggle from '../../hooks/useDescriptionToggle';
import useFadeInAnimation from '../../hooks/useFadeInAnimation';
import projectImages from '../../Config/ProjectImages';
import '../../styles/Projects.css';

const MAX_DESCRIPTION_LENGTH = 150;

const Projects = () => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const projectsRef = useRef(null);
    const t = translations[language].projects;
    
    // Usar hooks personalizados
    const { expandedDescriptions, toggleDescription } = useDescriptionToggle();
    const { activeFilter, setActiveFilter, filteredProjects, uniqueTechnologies } = useProjectFilter(t.list);
    
    // Usar hook de animación cuando cambian los proyectos filtrados
    useFadeInAnimation('.project-card', 0.1, [filteredProjects]);

    return (
        <section id="projects" className={`projects ${theme}`}>
            <Container>
                <h2 className="section-title">{t.title}</h2>
                
                {/* Filtro de tecnologías */}
                <div className="filter-container">
                    <div className="filter-title">
                        <FaFilter className="filter-icon" />
                        <span>{t.filterBy}</span>
                    </div>
                    <div className="filter-buttons">
                        <button 
                            className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('all')}
                        >
                            {t.allProjects || 'Todos'}
                        </button>
                        {uniqueTechnologies.map((tech, index) => (
                            <button 
                                key={index}
                                className={`filter-btn ${activeFilter === tech.name ? 'active' : ''}`}
                                onClick={() => setActiveFilter(tech.name)}
                                style={{ 
                                    backgroundColor: activeFilter === tech.name ? `${tech.color}30` : 'transparent',
                                    color: activeFilter === tech.name ? tech.color : 'inherit',
                                    borderColor: activeFilter === tech.name ? tech.color : 'var(--text-secondary)'
                                }}
                            >
                                {tech.name}
                            </button>
                        ))}
                    </div>
                </div>
                
                {/* Mensaje si no hay proyectos */}
                {filteredProjects.length === 0 && (
                    <div className="no-projects-message">
                        {t.noProjectsFound || 'No se encontraron proyectos con esta tecnología.'}
                    </div>
                )}

                {/* Grid de proyectos */}
                <div className="projects-grid" ref={projectsRef}>
                    {filteredProjects.map((project, index) => (
                        <div 
                            key={index}
                            className="project-card"
                            style={{ animationDelay: `${index * 0.2}s` }}
                        >
                            <div className="project-preview">
                                <img 
                                    src={projectImages[Object.keys(projectImages)[index % Object.keys(projectImages).length]]} 
                                    alt={project.title}
                                    className="project-image"
                                />
                                <div className="project-overlay">
                                    <div className="feature-list">
                                        <h4>{t.mainFeatures}</h4>
                                        <ul>
                                            {project.features.map((feature, i) => (
                                                <li key={i}>{feature}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="project-content">
                                <h3 className="project-title">{project.title}</h3>
                                <div className="project-description-container">
                                    <p className={`project-description ${expandedDescriptions[index] ? 'expanded' : ''}`}>
                                        {project.description}
                                    </p>
                                    {project.description.length > MAX_DESCRIPTION_LENGTH && (
                                        <button 
                                            className="read-more-btn"
                                            onClick={() => toggleDescription(index)}
                                        >
                                            {expandedDescriptions[index] ? (
                                                <>{t.showLess} <FaChevronUp /></>
                                            ) : (
                                                <>{t.readMore} <FaChevronDown /></>
                                            )}
                                        </button>
                                    )}
                                </div>
                                
                                <div className="tech-stack">
                                    {project.technologies.map((tech, i) => (
                                        <span 
                                            key={i} 
                                            className="tech-badge"
                                            style={{ 
                                                backgroundColor: `${tech.color}15`,
                                                color: tech.color,
                                                border: `1px solid ${tech.color}`
                                            }}
                                        >
                                            {tech.name}
                                        </span>
                                    ))}
                                </div>

                                <div className="project-links">
                                    <a href={project.githubUrl} className="project-link github">
                                        <FaGithub /> {t.links.code}
                                    </a>
                                    <a href={project.liveUrl} className="project-link demo">
                                        <FaExternalLinkAlt /> {t.links.demo}
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Container>
        </section>
    );
};

export default Projects;